export type PagePayload =
  | {
      url: string;
      title?: string;
      referrer?: string;
    }
  | {
      redacted: true;
      reason: string;
    };

export interface PageTrackerConfig {
  privacyPatterns?: string[];
  privacyParamBlacklist?: string[];
  push: (payload: PagePayload) => boolean;
  debug?: boolean;
}

export class PageTracker {
  private privacyPatterns: string[];
  private privacyParamBlacklist: string[];
  private push: (payload: PagePayload) => boolean;
  private debug: boolean;
  private debounceTimer: number | null = null;
  private lastSentUrl: string | null = null;
  private originalPushState: History['pushState'] | null = null;
  private originalReplaceState: History['replaceState'] | null = null;
  private started = false;

  constructor(config: PageTrackerConfig) {
    this.privacyPatterns = config.privacyPatterns || [];
    this.privacyParamBlacklist = config.privacyParamBlacklist || [];
    this.push = config.push;
    this.debug = config.debug || false;
  }

  start(): void {
    if (this.started || typeof window === 'undefined') return;

    this.started = true;
    this.patchHistory();
    window.addEventListener('popstate', this.scheduleEmit);
    window.addEventListener('hashchange', this.scheduleEmit);
    this.scheduleEmit();
  }

  stop(): void {
    if (!this.started || typeof window === 'undefined') return;

    this.started = false;

    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    window.removeEventListener('popstate', this.scheduleEmit);
    window.removeEventListener('hashchange', this.scheduleEmit);
    this.restoreHistory();
  }

  emitCurrentPage(): void {
    this.emitPageChanged();
  }

  private scheduleEmit = (): void => {
    if (typeof window === 'undefined') return;

    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      this.emitPageChanged();
    }, 500);
  };

  private emitPageChanged(): void {
    if (typeof window === 'undefined') return;

    const currentUrl = window.location.href;
    if (currentUrl === this.lastSentUrl) return;

    const payload = this.buildPagePayload(currentUrl, document.title);
    const pushed = this.push(payload);

    if (pushed) {
      this.lastSentUrl = currentUrl;
    } else if (this.debug) {
      console.log('[PageTracker] page change not pushed; conversation channel not ready');
    }
  }

  private buildPagePayload(url: string, title: string): PagePayload {
    const parsed = new URL(url);

    for (const pattern of this.privacyPatterns) {
      try {
        if (new RegExp(pattern).test(parsed.pathname)) {
          return { redacted: true, reason: 'path_blacklist' };
        }
      } catch (_error) {
        if (this.debug) {
          console.warn('[PageTracker] Ignoring invalid privacy regex', pattern);
        }
      }
    }

    for (const param of this.privacyParamBlacklist) {
      parsed.searchParams.delete(param);
    }

    return {
      url: parsed.toString(),
      title: sanitizeTitle(title),
      referrer: document.referrer || undefined,
    };
  }

  private patchHistory(): void {
    if (this.originalPushState || this.originalReplaceState) return;

    this.originalPushState = window.history.pushState;
    this.originalReplaceState = window.history.replaceState;

    const tracker = this;

    window.history.pushState = function (...args) {
      const result = tracker.originalPushState!.apply(this, args);
      tracker.scheduleEmit();
      return result;
    };

    window.history.replaceState = function (...args) {
      const result = tracker.originalReplaceState!.apply(this, args);
      tracker.scheduleEmit();
      return result;
    };
  }

  private restoreHistory(): void {
    if (this.originalPushState) {
      window.history.pushState = this.originalPushState;
      this.originalPushState = null;
    }

    if (this.originalReplaceState) {
      window.history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
  }
}

export function sanitizeTitle(raw: string): string {
  return (raw || '')
    .replace(/#\d{4,}/g, '#***')
    .replace(/[A-Z0-9]{10,}/g, '***')
    .slice(0, 500);
}

export function currentPageContext(): {
  page_url: string;
  page_title: string;
  referrer?: string;
} {
  return {
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer || undefined,
  };
}
